import React, { forwardRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

import Stethoscope, { StethoscopeProps, StethoscopeHandle } from '@/components/group/Stethoscope'
import consultationModel from '@/core/models/consultation.model'
import AudioRecordingTable, { AudioRecordingTableProps } from '@/components/ui/tables/AudioRecordingTable'

import { useDiagnosticRTC } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'

import DiagnosticRTCServiceSwitch from './DiagnosticRTCServiceSwitch'
import { useHDStethEcg } from '@/providers/diagnosticRTC/hd-steth-ecg-provider'

export type DiagnosticStethoscopeProps = Omit<
  StethoscopeProps,
  'audioInputMediaDevices' | 'isStreamer' | 'renderAudio' | 'renderVisualizer' | 'stethoscopes' | 'stream' | 'ecg'
> & {
  audioRecordings?: AudioRecordingTableProps['data']
  consultationId?: string
  externalAudioInputActive?: boolean
  externalSelectedDeviceId?: string
  onDeleteAnnotation?: AudioRecordingTableProps['onDeleteAnnotation']
  onUpdateAnnotation?: AudioRecordingTableProps['onUpdateAnnotation']
}

export const DiagnosticStethoscope = forwardRef<StethoscopeHandle, DiagnosticStethoscopeProps>(function DiagnosticStethoscope(props, ref) {
  const { audioRecordings, consultationId, externalAudioInputActive, externalSelectedDeviceId, onDeleteAnnotation, onUpdateAnnotation, ...rest } = props
  const {
    activeDiagnosticRTCService,
    changeDiagnosticRTCService,
    devices,
    diagnosticRTCServices,
    isStreamer,
    renderAudio,
    renderAudioVisualizer,
    selectDevice,
    stopMicro,
    stream,
  } = useDiagnosticRTC()
  const { t } = useTranslation()
  const ecg = useHDStethEcg()

  // trace the stethoscope activation on the consultation, so usage is measurable
  // even when the professional listens without recording
  const handleSelectDevice = useCallback(
    (deviceId: string) => {
      if (isStreamer && consultationId) {
        const stethoscope = devices.stethoscopes.find((candidate) => candidate.device.deviceId === deviceId)
        if (stethoscope) {
          consultationModel.reportDeviceUsage(consultationId, {
            deviceType: 'stethoscope',
            customName: stethoscope.customName,
            deviceLabel: stethoscope.device.label,
            trigger: 'manual',
          })
        }
      }
      selectDevice(deviceId)
    },
    [consultationId, devices.stethoscopes, isStreamer, selectDevice]
  )

  return (
    <Container maxWidth="md" sx={{ pt: '20px', pb: '20px' }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={{ fontSize: '18px', fontWeight: 500 }}>
            {t('page.liveConsultation.stethoscope.title')}
          </Typography>
        </Stack>
        <Stack alignItems="end">
          <DiagnosticRTCServiceSwitch onChange={changeDiagnosticRTCService} selectedService={activeDiagnosticRTCService} services={diagnosticRTCServices} />
          <Stethoscope
            {...rest}
            ecg={ecg}
            ref={ref}
            externalAudioInputActive={externalAudioInputActive}
            externalSelectedDeviceId={externalSelectedDeviceId}
            audioInputMediaDevices={devices.audioInputMediaDevices}
            isStreamer={isStreamer}
            onSelectDevice={handleSelectDevice}
            onStopMicro={() => { stopMicro(); rest.onStopMicro?.() }}
            renderAudio={renderAudio}
            renderVisualizer={renderAudioVisualizer}
            stethoscopes={devices.stethoscopes}
            stream={stream}
          />
        </Stack>
        <AudioRecordingTable data={audioRecordings} onDeleteAnnotation={onDeleteAnnotation} onUpdateAnnotation={onUpdateAnnotation} />
      </Stack>
    </Container>
  )
})

export default DiagnosticStethoscope
